import IAuthStrategy from "./IAuthStrategy.js";
import { getDeviceFingerprint } from "@/shared/auth/lib/deviceFingerprint";
import { tokenStorage } from "@/shared/auth/tokenStorage";

export default class NclayerAuthStrategy extends IAuthStrategy {
  constructor(authPort, storagePort, mode) {
    super();
    this.authPort = authPort;
    this.storagePort = storagePort;
    this.mode = mode;
  }

  /**
   * Этап 1: challenge → подпись nonce → login/init или login/registration
   * Возвращает: { _success, session_id, roles }
   *
   * Безопасность: подписывается серверный nonce (challenge-response).
   * Replay невозможен — nonce one-shot и имеет TTL.
   */
  async login() {
    try {
      const action = this.mode === "registration" ? "register" : "login";

      // 1) Параллельно: device fingerprint (best-effort) и challenge.
      //    На /login/challenge/ X-Device-Info не передаём — CORS на этом
      //    endpoint не whitelist'ит заголовок. Header идёт только в /login/init/.
      //    Fingerprint не должен блокировать логин — если ThumbmarkJS упал,
      //    идём без него (бэк сам решит как реагировать на пустой fingerprint).
      const [fp, challenge] = await Promise.all([
        getDeviceFingerprint().catch((e) => {
          console.warn("device fingerprint unavailable:", e?.message);
          return { raw: "", hash: "", info: "" };
        }),
        this.authPort.getChallenge(action),
      ]);
      const challengeFetchedAt = Date.now();

      // 3) Подписываем nonce строкой (контракт с бэком)
      const signed = await this.authPort.sign(challenge.nonce);

      const signatures = signed.signatures;
      if (!signatures || signatures.length === 0) {
        throw new Error("Не удалось подписать данные ЭЦП");
      }

      // 4) TTL guard: NCALayer-окно могло висеть долго — лучше явная ошибка с retry
      const expiresInMs = (challenge.expires_in || 0) * 1000;
      if (
        expiresInMs > 0 &&
        Date.now() - challengeFetchedAt >= expiresInMs - 5000
      ) {
        throw new Error("CHALLENGE_EXPIRED");
      }

      // 5) Отправляем /login/init или /login/registration/:
      //    body: nonce + device_fingerprint (hash для server-side equality compare).
      //    header: X-Device-Info — base64(JSON{timezone, browser, platform})
      //    для audit и DEVICE_BLOCKED diff на бэке.
      const initRes = await this.authPort.login(signatures[0], this.mode, {
        nonce: challenge.nonce,
        device_fingerprint: fp.raw,
        deviceInfo: fp.info,
      });

      // initRes = { _success, session_id, roles }
      return initRes;
    } catch (err) {
      console.error("Ошибка NclayerAuthStrategy.login():", err);
      return {
        _success: false,
        message: err.message || this.$t("Ошибка авторизации через ЭЦП"),
      };
    }
  }

  /**
   * Этап 2: Подтверждение роли
   * Вызывает: /login/confirm/
   * Возвращает: токены
   */
  async confirm(session_id, role_code) {
    try {
      const res = await this.authPort.confirm(session_id, role_code);

      if (res._success) {
        tokenStorage.setFromConfirm(res);
      }

      return res;
    } catch (err) {
      console.error("Ошибка NclayerAuthStrategy.confirm():", err);
      return {
        _success: false,
        message: err.message || this.$t("Ошибка подтверждения роли"),
      };
    }
  }

  /**
   * Logout — полностью очистить хранилище
   */
  async logout() {
    // Серверный logout: бэк помечает session_id revoked и сбрасывает
    // refresh-cookie. Не блокируем UX на ошибке — фронтовая зачистка важнее.
    try {
      await this.authPort.serverLogout();
    } catch (e) {
      console.warn(
        "Server logout failed, clearing client-side only:",
        e?.message
      );
    }
    tokenStorage.clear();
    this.clearSessionKeys(["redirectPath"]);
    window.location.reload();
  }

  /**
   * Обновление access-токена. Refresh-token летит автоматически в HttpOnly
   * cookie (см. NclayerAuthPort.refreshTokens — body пустое).
   */
  async refreshTokens() {
    try {
      const res = await this.authPort.refreshTokens();
      if (res?._success && res.access_token) {
        tokenStorage.setAccessToken(res.access_token);
      }
      return res;
    } catch (error) {
      console.error("Ошибка обновления токенов:", error);
      return {
        _success: false,
        message: this.$t("Ошибка обновления токенов"),
      };
    }
  }

  clearSessionKeys(keys = []) {
    keys.forEach((key) => sessionStorage.removeItem(key));
  }
}
